import * as Sentry from "@sentry/react";

/**
 * Browser errors that are not actionable and should be dropped.
 */
export function shouldDropEvent(exception: unknown): boolean {
  if (!exception) return false;
  // Sentry hands back whatever was thrown, which is not always an Error.
  const err = exception as { message?: string; name?: string };
  const msg = err.message || String(exception);
  const name = err.name || "";

  // Video play/pause race conditions (AbortError from media APIs)
  if (msg.includes("play() request was interrupted")) return true;

  // Generic AbortError (navigation, fetch abort, etc.)
  if (name === "AbortError") return true;

  // Autoplay blocked by browser policy
  if (name === "NotAllowedError") return true;

  // Video element with no playable source format (native or wrapped in Error)
  if (name === "NotSupportedError" || msg.includes("NotSupportedError"))
    return true;

  // Google Translate / browser extensions manipulating React DOM
  if (
    (msg.includes("insertBefore") || msg.includes("removeChild")) &&
    msg.includes("not a child of this node")
  )
    return true;

  return false;
}

// todo: https://docs.sentry.io/platforms/javascript/guides/react/#react-19-error-reporting
if (
  import.meta.env.VITE_ENVIRONMENT &&
  !import.meta.env.VITE_REACT_SUPPRESS_SENTRY
) {
  Sentry.init({
    dsn: "https://99b46a3a1969aca19dbad17dff15a007@o4506519499309056.ingest.us.sentry.io/4507435565056000",
    integrations: [
      // Sentry.replayIntegration(),
      // Sentry.feedbackIntegration({
      //   // Additional SDK configuration goes in here, for example:
      //   colorScheme: "system",
      // }),
    ],
    // Exceptions filtering
    beforeSend(event, hint) {
      if (shouldDropEvent(hint.originalException)) {
        return null;
      }
      if (
        hint.originalException instanceof DataCloneError &&
        hint.originalException.message.includes("The object can not be cloned.")
      ) {
        return null;
      }
      return event;
    },
    // Session Replay
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0.3,
    environment: import.meta.env.VITE_ENVIRONMENT,
  });
}

class DataCloneError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DataCloneError";
  }
}
